import React from "react";
import { Expression, FillPaint /*, LinePaint*/ } from "mapbox-gl";
import { Layer, LayerProps, MapEvent, MapRef, Source, SourceProps, ViewportProps } from "react-map-gl";
import _ from "lodash";

import MapBase from "./MapBase";

const HEX_10_FILL_LAYER_ID = "level-10-cells-fill";

export interface MapCell {
    id: String,
    colourGroup: number,
    opacity?: number
}

interface PointerInfo {
    cellId?: String,
    latitude: number,
    longitude: number
}

export const pointerInfoEmpty: PointerInfo = {
    cellId: undefined,
    latitude: 0,
    longitude: 0
};

interface HexLevel10MapProps {
    loading: boolean,
    error: boolean,
    title?: string,
    mapboxAccessToken: string,
    mapboxBaseMapStyle: string,
    mapboxHexLevel10TilesetId: string,
    mapboxHexLevel10TilesetUrl: string,
    height?: string | number,
    initialViewport: ViewportProps,
    cells: MapCell[],
    colours: string[]
    enableHover?: boolean,
    onHover?: (pointerInfo: PointerInfo) => void,
    enableClick?: boolean,
    onClick?: (pointerInfo: PointerInfo) => void,
    addGeocoder: boolean,
    geocoderContainerRef?: React.RefObject<HTMLDivElement>,
    children?: React.ReactNode,
    displayPOIs?: boolean,
    downloadData?: object[]
}

const HexLevel10Map: React.FC<HexLevel10MapProps> = (props) => {
    const {
        loading, error,
        title,
        mapboxAccessToken, mapboxBaseMapStyle, mapboxHexLevel10TilesetId, mapboxHexLevel10TilesetUrl,
        height,
        initialViewport,
        cells, colours,
        enableHover, onHover, enableClick, onClick,
        addGeocoder, geocoderContainerRef,
        displayPOIs, downloadData
    } = props;
    const mapRef = React.useRef<MapRef>(null);
    const [hoverInfo, setHoverInfo] = React.useState<PointerInfo>(pointerInfoEmpty);
    const [clickInfo, setClickInfo] = React.useState<PointerInfo>(pointerInfoEmpty);

    const hexLevel10Source = React.useMemo((): SourceProps => {
        return {
            type: "vector",
            url: mapboxHexLevel10TilesetUrl
        };
    }, [mapboxHexLevel10TilesetUrl]);

    const fillColor = React.useMemo(() => {
        let fillColor: string | Expression = "rgba(0,0,0,0)";
        if (cells.length > 0) {
            fillColor = ["match", ["get", "cellId"]];
            for (const cell of cells) {
                fillColor.push(cell.id, colours[cell.colourGroup]);
            }
            fillColor.push("rgba(0, 0, 0, 0)");
        }
        return fillColor;
    }, [cells, colours]);

    const fillOpacity = React.useMemo(() => {
        let fillOpacity: number | Expression = 0.4;
        const minOpacity = 0.1;
        const maxOpacity = 0.6;
        const standardOpacity = 0.4;
        const highlightOpacity = 0.7;

        if (cells.length > 0) {
            fillOpacity = ["match", ["get", "cellId"]];
            for (const cell of cells) {
                if (cell.id === hoverInfo.cellId || cell.id === clickInfo.cellId) {
                    fillOpacity.push(cell.id, highlightOpacity);
                } else {
                    fillOpacity.push(
                        cell.id,
                        cell.opacity ? (((cell.opacity) * (maxOpacity - minOpacity)) + minOpacity) : standardOpacity
                    );
                }
            }
            fillOpacity.push(maxOpacity);
        }
        return fillOpacity;
    }, [cells, hoverInfo, clickInfo]);

    const hexLevel10FillLayer = React.useMemo(() => {
        const paint: FillPaint = {
            "fill-color": fillColor,
            "fill-opacity": fillOpacity
        };
        const layer: LayerProps = {
            id: HEX_10_FILL_LAYER_ID,
            type: "fill",
            "source-layer": mapboxHexLevel10TilesetId,
            beforeId: "tunnel-simple",
            paint
        };
        return layer;
    }, [fillColor, fillOpacity, mapboxHexLevel10TilesetId]);

    const handlePointerEvent = React.useCallback((event: MapEvent): PointerInfo => {
        const { features, lngLat } = event;
        const cellId = features?.find(f => f.layer.id === HEX_10_FILL_LAYER_ID)?.properties["cellId"];
        if (!cells.some(cell => cell.id === cellId)) {
            return pointerInfoEmpty;
        }
        return {
            cellId: cellId,
            latitude: lngLat[1],
            longitude: lngLat[0]
        };
    }, [cells]);

    const handleClick = React.useCallback((event: MapEvent) => {
        if (!enableClick) {
            return;
        }
        const pointerInfo = handlePointerEvent(event);
        const newPointerInfo = pointerInfo.cellId === clickInfo.cellId ? pointerInfoEmpty : pointerInfo;
        setClickInfo(newPointerInfo);
        onClick && onClick(newPointerInfo);
    }, [enableClick, handlePointerEvent, clickInfo, setClickInfo, onClick]);

    const handleHover = React.useCallback((event: MapEvent) => {
        if (!enableHover) {
            return;
        }
        const pointerInfo = handlePointerEvent(event);
        setHoverInfo(pointerInfo);
        onHover && onHover(pointerInfo);
    }, [enableHover, handlePointerEvent, setHoverInfo, onHover]);

    const handleHoverDelayed = React.useMemo(() => {
        return _.debounce((event: MapEvent) => handleHover(event), 100);
    }, [handleHover]);

    const handleMouseOut = React.useCallback(() => {
        handleHoverDelayed.cancel();
        setHoverInfo(pointerInfoEmpty);
        onHover && onHover(pointerInfoEmpty);
    }, [handleHoverDelayed, setHoverInfo, onHover]);

    return (
        <MapBase
            loading={loading}
            error={error}
            title={title}
            mapboxAccessToken={mapboxAccessToken}
            mapboxBaseMapStyle={mapboxBaseMapStyle}
            height={height}
            mapRef={mapRef}
            initialViewport={initialViewport}
            addGeocoder={addGeocoder}
            geocoderContainerRef={geocoderContainerRef}
            addNavigationControl={true}
            addRecenterButton={true}
            addFullscreenButton={true}
            interactiveLayerIds={[HEX_10_FILL_LAYER_ID]}
            onHover={handleHoverDelayed}
            onClick={handleClick}
            onMouseOut={handleMouseOut}
            dataCy="hex-10-map"
            displayPOIs={displayPOIs}
            downloadData={downloadData}

        >
            <Source {...hexLevel10Source}>
                <Layer {...hexLevel10FillLayer} />
            </Source>
            {props.children}
        </MapBase>
    );
};

HexLevel10Map.defaultProps = {
    // ToDo: setting the values for retro-compatibility; bigger refactoring needed
    enableHover: true,
    enableClick: true
};

export default HexLevel10Map;
